import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  countPersons,
  exportTree,
  getDisplayName,
  getLifeSpan,
  getTree,
  listPersons,
  PersonSummary,
} from "../api/trees";
import Header from "../components/Common/Header";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import { AncestryTreeView } from "../components/Tree";

const ITEMS_PER_PAGE = 24;

type TabType = "people" | "tree" | "media";

export default function TreeView() {
  const { treeId } = useParams<{ treeId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("people");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sexFilter, setSexFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Debounce search
  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page on search
    const timeoutId = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(timeoutId);
  };

  // Reset page when filters change
  const handleSexFilter = (value: string) => {
    setSexFilter(value);
    setCurrentPage(1);
  };

  const { data: tree, isLoading: treeLoading } = useQuery({
    queryKey: ["tree", treeId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getTree(Number(treeId), token);
    },
    enabled: !!treeId,
  });

  const {
    data: persons,
    isLoading: personsLoading,
    error,
  } = useQuery({
    queryKey: ["persons", treeId, debouncedSearch, sexFilter, currentPage],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listPersons(Number(treeId), token, {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        search: debouncedSearch || undefined,
        sex: sexFilter || undefined,
      });
    },
    enabled: !!treeId,
  });

  const { data: totalCount } = useQuery({
    queryKey: ["personsCount", treeId, debouncedSearch, sexFilter],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return countPersons(Number(treeId), token, {
        search: debouncedSearch || undefined,
        sex: sexFilter || undefined,
      });
    },
    enabled: !!treeId,
  });

  const totalPages = totalCount ? Math.ceil(totalCount / ITEMS_PER_PAGE) : 1;

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return exportTree(Number(treeId), token);
    },
    onSuccess: ({ content, filename }) => {
      // Create download link
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  const isLoading = treeLoading || personsLoading;

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <ol className="flex items-center gap-2 text-sm text-vault-600">
            <li>
              <Link to="/dashboard" className="hover:text-vault-900">
                Dashboard
              </Link>
            </li>
            <li>/</li>
            <li className="text-vault-900">{tree?.name || "Tree"}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-vault-900">
              {tree?.name || "Loading..."}
            </h1>
            {tree && (
              <p className="mt-1 text-vault-600">
                {tree.person_count} people in this tree
              </p>
            )}
          </div>
          <button
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending || !tree}
            className="btn-secondary flex items-center gap-2"
          >
            {exportMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
            )}
            Export GEDCOM
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-vault-200">
          <nav className="-mb-px flex gap-4">
            <button
              onClick={() => setActiveTab("people")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === "people"
                  ? "border-vault-600 text-vault-900"
                  : "border-transparent text-vault-500 hover:border-vault-300 hover:text-vault-700"
              }`}
            >
              People List
            </button>
            <button
              onClick={() => setActiveTab("tree")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === "tree"
                  ? "border-vault-600 text-vault-900"
                  : "border-transparent text-vault-500 hover:border-vault-300 hover:text-vault-700"
              }`}
            >
              Tree View
            </button>
            <button
              onClick={() => navigate(`/trees/${treeId}/media`)}
              className="border-b-2 border-transparent px-1 pb-3 text-sm font-medium text-vault-500 transition-colors hover:border-vault-300 hover:text-vault-700"
            >
              Media Library
            </button>
          </nav>
        </div>

        {/* Tree View Tab */}
        {activeTab === "tree" ? (
          <AncestryTreeView treeId={Number(treeId)} />
        ) : (
        <>
        {/* Search and Filters */}
        <div className="mb-6 space-y-3">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-vault-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name or place..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-lg border border-vault-300 py-2 pl-10 pr-4 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                showFilters || sexFilter
                  ? "border-vault-500 bg-vault-50 text-vault-700"
                  : "border-vault-300 text-vault-600 hover:border-vault-400"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                />
              </svg>
              Filters
              {sexFilter && (
                <span className="rounded-full bg-vault-600 px-1.5 py-0.5 text-xs text-white">
                  1
                </span>
              )}
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="rounded-lg border border-vault-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">Sex</label>
                  <select
                    value={sexFilter}
                    onChange={(e) => handleSexFilter(e.target.value)}
                    className="mt-1 rounded-lg border border-vault-300 px-3 py-1.5 text-sm focus:border-vault-500 focus:outline-none"
                  >
                    <option value="">All</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="U">Unknown</option>
                  </select>
                </div>
                {sexFilter && (
                  <button
                    onClick={() => {
                      setSexFilter("");
                    }}
                    className="mt-5 text-sm text-vault-600 hover:text-vault-900"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Results count */}
          {totalCount !== undefined && (
            <p className="text-sm text-vault-500">
              {totalCount} {totalCount === 1 ? "person" : "people"} found
              {(debouncedSearch || sexFilter) && " matching filters"}
            </p>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            Failed to load persons. Please try again.
          </div>
        ) : !persons || persons.length === 0 ? (
          <div className="rounded-lg border border-vault-200 bg-white p-8 text-center">
            <p className="text-vault-600">
              {debouncedSearch
                ? `No persons found matching "${debouncedSearch}"`
                : "No persons in this tree yet."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {persons.map((person) => (
                <PersonCard
                  key={person.id}
                  person={person}
                  treeId={Number(treeId)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg border border-vault-300 px-3 py-1.5 text-sm text-vault-600 hover:bg-vault-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`h-8 w-8 rounded-lg text-sm ${
                          currentPage === pageNum
                            ? "bg-vault-600 text-white"
                            : "text-vault-600 hover:bg-vault-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-vault-300 px-3 py-1.5 text-sm text-vault-600 hover:bg-vault-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        </>
        )}
      </main>
    </div>
  );
}

function PersonCard({
  person,
  treeId,
}: {
  person: PersonSummary;
  treeId: number;
}) {
  const displayName = getDisplayName(person);
  const lifeSpan = getLifeSpan(person);

  return (
    <Link
      to={`/trees/${treeId}/persons/${person.id}`}
      className="card group transition-shadow hover:shadow-md"
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white ${
            person.sex === "M"
              ? "bg-blue-500"
              : person.sex === "F"
                ? "bg-pink-500"
                : "bg-vault-400"
          }`}
        >
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-medium text-vault-900 group-hover:text-vault-700">
            {displayName}
          </h3>
          {lifeSpan && (
            <p className="text-sm text-vault-500">{lifeSpan}</p>
          )}
          {person.birth_place_text && (
            <p className="mt-1 truncate text-xs text-vault-400">
              {person.birth_place_text}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

